import { useState, useEffect } from 'react';
import {
  Shield,
  Users,
  Bell,
  History,
  Search,
  Crown,
  ChevronLeft,
  Send,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  DollarSign,
  Tag,
  Plus,
  Edit2,
  Trash2,
  Sparkles,
  PartyPopper,
  Gift,
  Clock,
  User,
  MessageSquare
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import FestivalThemeSelector from '../components/FestivalThemeSelector';
import { useFestivalTheme, festivalThemes } from '../contexts/FestivalThemeContext';

interface User {
  id: string;
  user_id: string;
  name: string;
  is_admin: boolean;
  created_at: string;
  email?: string;
}

interface NotificationData {
  id: string;
  title: string;
  body: string;
  recipient_filter: string;
  recipient_count: number;
  delivery_count: number;
  failed_count: number;
  created_at: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  duration_months: number;
  price: number;
  is_active: boolean;
  created_at: string;
}

interface CouponCode {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  expiration_date: string | null;
  usage_limit: number | null;
  times_used: number;
  is_active: boolean;
  created_at: string;
}

interface GiftSubscription {
  id: string;
  gifted_by_name: string;
  recipient_email: string;
  duration_months: number;
  message: string | null;
  status: 'pending' | 'redeemed' | 'expired' | 'cancelled';
  created_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
}

interface AdminPanelProps {
  onBack: () => void;
}

type TabType = 'users' | 'send' | 'history' | 'pricing' | 'themes' | 'gifts';

export default function AdminPanel({ onBack }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [coupons, setCoupons] = useState<CouponCode[]>([]);
  const [gifts, setGifts] = useState<GiftSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  
  // Gift form state
  const [giftEmail, setGiftEmail] = useState('');
  const [giftDuration, setGiftDuration] = useState(1);
  const [giftMessage, setGiftMessage] = useState('');
  const [giftExpiresInDays, setGiftExpiresInDays] = useState(30);
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftSuccess, setGiftSuccess] = useState('');
  const [giftError, setGiftError] = useState('');
  const [giftFilterStatus, setGiftFilterStatus] = useState<string>('all');
  
  const { currentTheme, currentThemeId } = useFestivalTheme();

  const [notificationForm, setNotificationForm] = useState({
    title: '',
    body: '',
    url: '/',
    recipientFilter: 'all' as 'all' | 'admins'
  });

  const [stats, setStats] = useState({
    totalUsers: 0,
    adminUsers: 0,
    totalNotifications: 0,
    activeSubscriptions: 0
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        await loadUsers();
        await loadStats();
      } else if (activeTab === 'history') {
        await loadNotifications();
      } else if (activeTab === 'pricing') {
        await loadPlans();
        await loadCoupons();
      } else if (activeTab === 'gifts') {
        await loadGifts();
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    const { data: profilesData, error: profilesError } = await supabase
      .from('user_profiles')
      .select('id, user_id, name, is_admin, created_at')
      .order('created_at', { ascending: false });

    if (profilesError) {
      console.error('Error loading users:', profilesError);
      return;
    }

    const userIds = profilesData.map(p => p.user_id);
    const { data: authData } = await supabase.auth.admin.listUsers();

    const usersWithEmail = profilesData.map(profile => {
      const authUser = authData?.users.find(u => u.id === profile.user_id);
      return {
        ...profile,
        email: authUser?.email || 'No email'
      };
    });

    setUsers(usersWithEmail);
  };

  const loadStats = async () => {
    const { count: totalUsers } = await supabase
      .from('user_profiles')
      .select('*', { count: 'exact', head: true });

    const { count: adminUsers } = await supabase
      .from('user_profiles')
      .select('*', { count: 'exact', head: true })
      .eq('is_admin', true);

    const { count: totalNotifications } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true });

    const { count: activeSubscriptions } = await supabase
      .from('push_subscriptions')
      .select('*', { count: 'exact', head: true });

    setStats({
      totalUsers: totalUsers || 0,
      adminUsers: adminUsers || 0,
      totalNotifications: totalNotifications || 0,
      activeSubscriptions: activeSubscriptions || 0
    });
  };

  const loadNotifications = async () => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('Error loading notifications:', error);
      return;
    }

    setNotifications(data || []);
  };

  const loadPlans = async () => {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .order('duration_months', { ascending: true });

    if (error) {
      console.error('Error loading plans:', error);
      return;
    }

    setPlans(data || []);
  };

  const loadCoupons = async () => {
    const { data, error } = await supabase
      .from('coupon_codes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading coupons:', error);
      return;
    }

    setCoupons(data || []);
  };

  const loadGifts = async () => {
    try {
      const { data, error } = await supabase.rpc('get_all_gift_subscriptions');
      
      if (error) throw error;
      
      if (data?.success) {
        setGifts(data.gifts || []);
      }
    } catch (err) {
      console.error('Error loading gifts:', err);
    }
  };

  const handleGiftPro = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!giftEmail.trim()) {
      setGiftError('Please enter recipient email');
      return;
    }
    
    setGiftLoading(true);
    setGiftError('');
    setGiftSuccess('');
    
    try {
      const { data, error: rpcError } = await supabase.rpc('create_gift_subscription', {
        p_recipient_email: giftEmail.toLowerCase().trim(),
        p_duration_months: giftDuration,
        p_message: giftMessage.trim() || null,
        p_expires_in_days: giftExpiresInDays
      });
      
      if (rpcError) throw rpcError;
      
      if (data?.success) {
        setGiftSuccess(`Pro plan gifted successfully to ${giftEmail}!`);
        setGiftEmail('');
        setGiftMessage('');
        loadGifts();
      } else {
        setGiftError(data?.error || 'Failed to gift subscription');
      }
    } catch (err: any) {
      console.error('Error gifting subscription:', err);
      setGiftError(err.message || 'Failed to gift subscription');
    } finally {
      setGiftLoading(false);
    }
  };

  const handleCancelGift = async (giftId: string) => {
    if (!confirm('Are you sure you want to cancel this gift?')) return;
    
    try {
      const { data, error } = await supabase.rpc('cancel_gift_subscription', {
        p_gift_id: giftId
      });
      
      if (error) throw error;
      
      if (data?.success) {
        loadGifts();
      } else {
        alert(data?.error || 'Failed to cancel gift');
      }
    } catch (err) {
      console.error('Error cancelling gift:', err);
      alert('Failed to cancel gift');
    }
  };

  const getGiftStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock size={12} />
            Pending
          </span>
        );
      case 'redeemed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle size={12} />
            Redeemed
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <XCircle size={12} />
            Expired
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle size={12} />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const filteredGifts = giftFilterStatus === 'all' 
    ? gifts 
    : gifts.filter(g => g.status === giftFilterStatus);

  const updatePlanPrice = async (planId: string, newPrice: number) => {
    try {
      const { error } = await supabase
        .from('subscription_plans')
        .update({ price: newPrice })
        .eq('id', planId);

      if (error) throw error;

      setPlans(plans.map(plan =>
        plan.id === planId ? { ...plan, price: newPrice } : plan
      ));

      setEditingPlan(null);
      alert('Plan price updated successfully!');
    } catch (error) {
      console.error('Error updating plan price:', error);
      alert('Failed to update plan price');
    }
  };

  const toggleCouponStatus = async (couponId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('coupon_codes')
        .update({ is_active: !currentStatus })
        .eq('id', couponId);

      if (error) throw error;

      setCoupons(coupons.map(coupon =>
        coupon.id === couponId ? { ...coupon, is_active: !currentStatus } : coupon
      ));
    } catch (error) {
      console.error('Error updating coupon status:', error);
      alert('Failed to update coupon status');
    }
  };

  const deleteCoupon = async (couponId: string) => {
    if (!confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const { error } = await supabase
        .from('coupon_codes')
        .delete()
        .eq('id', couponId);

      if (error) throw error;

      setCoupons(coupons.filter(coupon => coupon.id !== couponId));
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert('Failed to delete coupon');
    }
  };

  const createCoupon = async (couponData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error } = await supabase
        .from('coupon_codes')
        .insert({
          ...couponData,
          created_by: user.id
        });

      if (error) throw error;

      await loadCoupons();
      setShowCreateCoupon(false);
      alert('Coupon created successfully!');
    } catch (error) {
      console.error('Error creating coupon:', error);
      alert('Failed to create coupon');
    }
  };

  const toggleAdminRole = async (userId: string, currentAdminStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_admin: !currentAdminStatus })
        .eq('user_id', userId);

      if (error) throw error;

      setUsers(users.map(user =>
        user.user_id === userId ? { ...user, is_admin: !currentAdminStatus } : user
      ));

      setStats(prev => ({
        ...prev,
        adminUsers: currentAdminStatus ? prev.adminUsers - 1 : prev.adminUsers + 1
      }));
    } catch (error) {
      console.error('Error updating admin role:', error);
      alert('Failed to update admin role');
    }
  };

  const sendNotification = async () => {
    if (!notificationForm.title.trim() || !notificationForm.body.trim()) {
      alert('Please enter title and message');
      return;
    }

    setSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-notification`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: notificationForm.title,
          body: notificationForm.body,
          url: notificationForm.url,
          recipientFilter: notificationForm.recipientFilter
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }

      const result = await response.json();

      alert(`Notification sent to ${result.recipientCount} users!`);

      setNotificationForm({
        title: '',
        body: '',
        url: '/',
        recipientFilter: 'all'
      });
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with current theme indicator */}
      <div 
        className="text-white px-6 py-6"
        style={{
          background: currentThemeId !== 'none'
            ? `linear-gradient(135deg, ${currentTheme.gradientFrom}, ${currentTheme.gradientTo})`
            : 'linear-gradient(135deg, #2563eb, #06b6d4)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8" />
            <h1 className="text-3xl font-bold">Admin Panel</h1>
            {currentThemeId !== 'none' && (
              <span className="ml-3 px-3 py-1 bg-white/20 rounded-full text-sm font-medium flex items-center gap-2">
                {currentTheme.emoji} {currentTheme.name} Theme Active
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5" />
                <span className="text-sm opacity-90">Total Users</span>
              </div>
              <div className="text-3xl font-bold">{stats.totalUsers}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5" />
                <span className="text-sm opacity-90">Admins</span>
              </div>
              <div className="text-3xl font-bold">{stats.adminUsers}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Bell className="w-5 h-5" />
                <span className="text-sm opacity-90">Notifications Sent</span>
              </div>
              <div className="text-3xl font-bold">{stats.totalNotifications}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm opacity-90">Active Subscriptions</span>
              </div>
              <div className="text-3xl font-bold">{stats.activeSubscriptions}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Updated tabs with Gift Pro Plan */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto pb-px">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'users'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              User Management
            </div>
          </button>
          <button
            onClick={() => setActiveTab('send')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'send'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Send className="w-5 h-5" />
              Send Notification
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'history'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Notification History
            </div>
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'pricing'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Pricing & Coupons
            </div>
          </button>
          <button
            onClick={() => setActiveTab('themes')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'themes'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <PartyPopper className="w-5 h-5" />
              Festival Themes
              {currentThemeId !== 'none' && (
                <span className="ml-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              )}
            </div>
          </button>
          {/* NEW: Gift Pro Plan Tab */}
          <button
            onClick={() => setActiveTab('gifts')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'gifts'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Gift Pro Plan
            </div>
          </button>
        </div>

        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading users...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Joined</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Role</th>
                      <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{user.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600">{user.email}</td>
                        <td className="px-6 py-4 text-gray-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {user.is_admin ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                              <Crown className="w-4 h-4" />
                              Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                              User
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => toggleAdminRole(user.user_id, user.is_admin)}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                              user.is_admin
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                          >
                            {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredUsers.length === 0 && (
                  <div className="p-12 text-center">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No users found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'send' && (
          <div className="bg-white rounded-xl shadow-sm p-8 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Send Push Notification</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                  placeholder="Notification title"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={notificationForm.body}
                  onChange={(e) => setNotificationForm({ ...notificationForm, body: e.target.value })}
                  placeholder="Notification message"
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target URL
                </label>
                <input
                  type="text"
                  value={notificationForm.url}
                  onChange={(e) => setNotificationForm({ ...notificationForm, url: e.target.value })}
                  placeholder="/dashboard"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients
                </label>
                <select
                  value={notificationForm.recipientFilter}
                  onChange={(e) => setNotificationForm({ ...notificationForm, recipientFilter: e.target.value as 'all' | 'admins' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Users</option>
                  <option value="admins">Admins Only</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Preview</h3>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 mb-1">
                        {notificationForm.title || 'Notification title'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {notificationForm.body || 'Notification message will appear here'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={sendNotification}
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Notification
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-xl shadow-sm">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading notifications...</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div key={notification.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg mb-1">
                          {notification.title}
                        </h3>
                        <p className="text-gray-600">{notification.body}</p>
                      </div>
                      <span className="text-sm text-gray-500 whitespace-nowrap ml-4">
                        {new Date(notification.created_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {notification.recipient_count} recipients
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-gray-600">
                          {notification.delivery_count} delivered
                        </span>
                      </div>
                      {notification.failed_count > 0 && (
                        <div className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-600" />
                          <span className="text-gray-600">
                            {notification.failed_count} failed
                          </span>
                        </div>
                      )}
                      <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {notification.recipient_filter}
                      </span>
                    </div>
                  </div>
                ))}

                {notifications.length === 0 && (
                  <div className="p-12 text-center">
                    <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No notifications sent yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-blue-600" />
                Subscription Plans
              </h2>

              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading plans...</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {plans.map((plan) => (
                    <div key={plan.id} className="border border-gray-200 rounded-lg p-5 hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-lg text-gray-900">{plan.name}</h3>
                          <p className="text-sm text-gray-600">{plan.duration_months} months duration</p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              plan.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {plan.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {editingPlan?.id === plan.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-2xl font-bold text-gray-400">$</span>
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={plan.price}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const input = e.target as HTMLInputElement;
                                    updatePlanPrice(plan.id, parseFloat(input.value));
                                  }
                                }}
                                className="w-24 px-3 py-2 border border-blue-500 rounded-lg text-2xl font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={() => setEditingPlan(null)}
                                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="text-3xl font-black text-gray-900">${plan.price}</span>
                              <button
                                onClick={() => setEditingPlan(plan)}
                                className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
                                title="Edit price"
                              >
                                <Edit2 className="w-5 h-5 text-blue-700" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Tag className="w-6 h-6 text-orange-600" />
                  Coupon Codes
                </h2>
                <button
                  onClick={() => setShowCreateCoupon(!showCreateCoupon)}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Coupon
                </button>
              </div>

              {showCreateCoupon && (
                <div className="mb-6 p-5 border-2 border-orange-200 rounded-lg bg-orange-50">
                  <h3 className="font-bold text-gray-900 mb-4">Create New Coupon</h3>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    createCoupon({
                      code: formData.get('code'),
                      discount_type: formData.get('discount_type'),
                      discount_value: parseFloat(formData.get('discount_value') as string),
                      expiration_date: formData.get('expiration_date') || null,
                      usage_limit: formData.get('usage_limit') ? parseInt(formData.get('usage_limit') as string) : null,
                      is_active: true
                    });
                  }}>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Coupon Code
                        </label>
                        <input
                          type="text"
                          name="code"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
                          placeholder="SUMMER2024"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Discount Type
                        </label>
                        <select
                          name="discount_type"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                          <option value="percentage">Percentage</option>
                          <option value="fixed">Fixed Amount</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Discount Value
                        </label>
                        <input
                          type="number"
                          name="discount_value"
                          required
                          step="0.01"
                          min="0"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="10"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Usage Limit (optional)
                        </label>
                        <input
                          type="number"
                          name="usage_limit"
                          min="1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="Unlimited"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Expiration Date (optional)
                        </label>
                        <input
                          type="date"
                          name="expiration_date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
                      >
                        Create Coupon
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateCoupon(false)}
                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-orange-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading coupons...</p>
                </div>
              ) : coupons.length === 0 ? (
                <div className="p-12 text-center">
                  <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No coupons created yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Code</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Discount</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Usage</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Expiration</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {coupons.map((coupon) => {
                        const isExpired = coupon.expiration_date && new Date(coupon.expiration_date) < new Date();
                        const isLimitReached = coupon.usage_limit && coupon.times_used >= coupon.usage_limit;

                        return (
                          <tr key={coupon.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <span className="font-mono font-bold text-gray-900">{coupon.code}</span>
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {coupon.discount_type === 'percentage'
                                ? `${coupon.discount_value}%`
                                : `$${coupon.discount_value}`}
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {coupon.times_used}{coupon.usage_limit ? ` / ${coupon.usage_limit}` : ' / ∞'}
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {coupon.expiration_date
                                ? new Date(coupon.expiration_date).toLocaleDateString()
                                : 'Never'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                isExpired || isLimitReached
                                  ? 'bg-red-100 text-red-700'
                                  : coupon.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {isExpired ? 'Expired' : isLimitReached ? 'Limit Reached' : coupon.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => toggleCouponStatus(coupon.id, coupon.is_active)}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    coupon.is_active
                                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                  disabled={isExpired || isLimitReached}
                                >
                                  {coupon.is_active ? 'Deactivate' : 'Activate'}
                                </button>
                                <button
                                  onClick={() => deleteCoupon(coupon.id)}
                                  className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                                  title="Delete coupon"
                                >
                                  <Trash2 className="w-4 h-4 text-red-700" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Festival Themes Tab Content */}
        {activeTab === 'themes' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <PartyPopper className="w-6 h-6 text-purple-600" />
                Festival Themes
              </h2>
              <p className="text-gray-600 mt-1">
                Select a festival theme to decorate the app for all users. The theme will appear across the home page and hero section.
              </p>
            </div>
            
            <FestivalThemeSelector />
          </div>
        )}

        {/* NEW: Gift Pro Plan Tab Content */}
        {activeTab === 'gifts' && (
          <div className="space-y-6">
            {/* Gift Form */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Gift className="w-6 h-6 text-purple-600" />
                Gift Pro Plan to User
              </h2>

              <form onSubmit={handleGiftPro} className="space-y-4">
                {/* Recipient Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Email *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={giftEmail}
                      onChange={(e) => setGiftEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    If user doesn't exist yet, the gift will be available when they sign up with this email.
                  </p>
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (months) *
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 3, 6, 12].map((months) => (
                      <button
                        key={months}
                        type="button"
                        onClick={() => setGiftDuration(months)}
                        className={`py-3 px-4 rounded-lg font-medium text-sm transition-all ${
                          giftDuration === months
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {months} {months === 1 ? 'Month' : 'Months'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or enter custom duration
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="24"
                      value={giftDuration}
                      onChange={(e) => setGiftDuration(parseInt(e.target.value) || 1)}
                      className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <span className="text-gray-600">months</span>
                  </div>
                </div>

                {/* Gift expires in */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gift expires in (days)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={giftExpiresInDays}
                      onChange={(e) => setGiftExpiresInDays(parseInt(e.target.value) || 30)}
                      className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <span className="text-gray-600">days to redeem</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    User must redeem the gift within this time period.
                  </p>
                </div>

                {/* Personal Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Message (optional)
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <textarea
                      value={giftMessage}
                      onChange={(e) => setGiftMessage(e.target.value)}
                      placeholder="Congratulations! You've been gifted Pro access..."
                      rows={3}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Error/Success Messages */}
                {giftError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700 text-sm font-medium">{giftError}</p>
                  </div>
                )}

                {giftSuccess && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                      <CheckCircle size={18} />
                      {giftSuccess}
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={giftLoading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {giftLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending Gift...
                    </>
                  ) : (
                    <>
                      <Gift className="w-5 h-5" />
                      Gift {giftDuration} {giftDuration === 1 ? 'Month' : 'Months'} Pro Plan
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Gift History */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Crown className="w-6 h-6 text-yellow-500" />
                  Gift History
                </h2>
                
                {/* Filter */}
                <select
                  value={giftFilterStatus}
                  onChange={(e) => setGiftFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Gifts</option>
                  <option value="pending">Pending</option>
                  <option value="redeemed">Redeemed</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading gifts...</p>
                </div>
              ) : filteredGifts.length === 0 ? (
                <div className="p-12 text-center">
                  <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No gifts found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Recipient</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Duration</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Created</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Expires/Redeemed</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredGifts.map((gift) => (
                        <tr key={gift.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{gift.recipient_email}</p>
                              {gift.message && (
                                <p className="text-xs text-gray-500 truncate max-w-[200px]" title={gift.message}>
                                  "{gift.message}"
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className="font-semibold text-purple-600">
                              {gift.duration_months} {gift.duration_months === 1 ? 'month' : 'months'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {getGiftStatusBadge(gift.status)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {new Date(gift.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {gift.status === 'redeemed' 
                              ? `Redeemed ${new Date(gift.redeemed_at!).toLocaleDateString()}`
                              : gift.expires_at 
                                ? new Date(gift.expires_at).toLocaleDateString()
                                : 'Never'
                            }
                          </td>
                          <td className="px-4 py-4 text-right">
                            {gift.status === 'pending' && (
                              <button
                                onClick={() => handleCancelGift(gift.id)}
                                className="p-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                                title="Cancel gift"
                              >
                                <Trash2 className="w-4 h-4 text-red-700" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}